/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useRef, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import Shimmer from 'react-shimmer-effect';
import Text from '../../../../shared/components/Text/Text';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';

import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  swapProgrammedItemOnChildCalendar,
} from '../../ducks/calendarActions';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  visibility: ${(props) => (props.hidden ? 'hidden' : 'visible')};
`;

const SessionBreakInput = styled('input')`
  border: none;
  border-radius: 4px;
  max-height: 34px;
  background: transparent;
  color: #424242;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;


   &::placeholder {
    font-family: 'Nunito Sans', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #BDBDBD;
  }

   &:focus {
    background: #FFFFFF;
    outline: none; /* optional: removes default blue outline */
    color: #BDBDBD;
  }
`;

const SessionBreakInputShimmer = styled('input')`
  width: 100%;
  border: none;
  border-radius: 5px;
  min-height: 34px;
  max-height: 34px;
`;

const NameAndInputContainer = styled('div')`
  display: flex;
  gap: 8px;
  align-items: center;
  .checkbox-input {
    appearance: none; /* Remove default checkbox styling */
    min-width: 18px;
    min-height: 18px;
    max-width: 18px;
    max-height: 18px;
    border: 2px solid #E0E0E0;
    border-radius: 4px;
    outline: none;
    cursor: ${(props) => (props.cursorNotAllowed ? 'not-allowed' : 'pointer')};
    position: relative;
    transition: ${TransitionGlobal};
    :hover {
      opacity: 0.7;
    }
  }
  .checkbox-input:checked {
    border-color: #77A7FF; /* blue border when checked */
  }

  .checkbox-input:checked::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 5px;
    width: 5px;
    height: 10px;
    border: solid #77A7FF; /* blue checkmark */
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: block;
  }
`;

const alreadySavedGlobal = new Set();

const SessionBreakTable = ({
  contentIndex,
  nonSwappedItemFromParentCalendar,
  content,
  checkBoxInput,
  setSessionBreakInputOpen,
  sessionBreakInputValue,
  setSessionBreakInputValue,
  inputBox,
  date,
  dayNumber,
  hidden,
}) => {
  const inputRef = useRef(null);
  const [isAdding, setIsAdding] = useState(false);
  const [collapseWorkoutItems, setCollaseWorkoutItems] = useState(!content?.showSessionBreakItems);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;

  const dispatch = useDispatch();

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const handleAutoSaveCalendarSessionBreak = (contentIndexParam) => {
    setIsAdding(true);
    const submissionObject = {
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
          programType === 'free-form-program'
            ? dayNumber
            : moment(effectiveDate).format('YYYY-MM-DD'),
      type: 'session_break',
      showSessionBreakItems: true,
    };
    if (content.description) {
      submissionObject.description = content.description;
    }
    const formattedDate = moment(effectiveDate).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        errorAddReset,
        { onSuccess: () => setIsAdding(false) },
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        errorAddReset,
        { onSuccess: () => setIsAdding(false) },
        content.assignedId,
      ));
    }
  };

  const debouncedHandleUpdateSessionBreak = useRef(
    debounce((
      dbSessionBreakInputValue,
      dbNonSwappedItemFromParentCalendar,
      dbCollapseWorkoutItems,
      reorder = false,
    ) => {
      const updateItem = {
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : moment(effectiveDate).format('YYYY-MM-DD'),
        order: contentIndex,
        description: content?.exercise?.description || '',
        type: 'session_break',
        showSessionBreakItems: !dbCollapseWorkoutItems,
      };
      if (programType === 'free-form-program') {
        updateItem.dayNumber = dayNumber;
      }
      if (dbSessionBreakInputValue) {
        updateItem.title = dbSessionBreakInputValue;
      }
      if (programType === 'free-form-program') {
        dispatch(updateSavedWorkoutItem(currentUser.accountCode, updateItem, contentIndex, dayNumber, reorder, {
          onSuccess: () => {
            setSessionBreakInputValue('');
          },
          onError: () => {},
        }));
      }
      if (programType === 'calendar') {
        if (dbNonSwappedItemFromParentCalendar) {
          dispatch(swapProgrammedItemOnChildCalendar(currentUser.accountCode, { ...updateItem }, contentIndex, moment(effectiveDate).format('YYYY-MM-DD'), reorder, {
            onSuccess: () => {
              setSessionBreakInputValue('');
            },
            onError: () => {},
          }));
        } else {
          dispatch(updateProgrammedCalendarItem(currentUser.accountCode, updateItem, contentIndex, moment(effectiveDate).format('YYYY-MM-DD'), reorder, {
            onSuccess: () => {
              setSessionBreakInputValue('');
            },
            onError: () => {},
          }));
        }
      }
    }, 2000),
  ).current;

  const handleFlushDebounce = () => {
    debouncedHandleUpdateSessionBreak.flush();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && sessionBreakInputValue?.trim()) {
      e.preventDefault();

      /** Force the debounced update to run immediately */
      debouncedHandleUpdateSessionBreak.flush();

      e.target.blur();
      setSessionBreakInputOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setSessionBreakInputOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /** the CellMeasurer wrapped around each row in the virtualized list
   * can cause remounting of the item a few times as it measures
   * the "just in time" height, this can cause multiple firings of this
   * handleAutoSave funtion (which is a problem because it will create 3 of the same items in the data base).
   * To get around that we save the item by id
   * in the global set that will persist renders and if we have alreay saved
   * the item them we will not fire that function again.
   */
  useEffect(() => {
    if (content.preSave && inputBox && !alreadySavedGlobal.has(content.assignedId)) {
      alreadySavedGlobal.add(content.assignedId);
      handleAutoSaveCalendarSessionBreak(contentIndex);
    }
  }, [content.assignedId]);

  const setSessionBreakInputValueFunction = (value) => {
    setSessionBreakInputValue(value);
    debouncedHandleUpdateSessionBreak(value, nonSwappedItemFromParentCalendar, collapseWorkoutItems);
  };

  return (
    <>
      {checkBoxInput ? (
        <Container onMouseLeave={handleFlushDebounce} hidden={hidden}>
          {!isAdding ? (
            <NameAndInputContainer>
              <input
                className='checkbox-input'
                type='checkbox'
                name='collapse workout item'
                onChange={(e) => {
                  setCollaseWorkoutItems(!collapseWorkoutItems);
                  debouncedHandleUpdateSessionBreak(sessionBreakInputValue, nonSwappedItemFromParentCalendar, e.target.checked);
                }}
                checked={collapseWorkoutItems}
              />
              <Text fontSize='16px' style={{ whiteSpace: 'nowrap ' }}>Collapse workout items under session break for athletes</Text>
            </NameAndInputContainer>
          ) : (
            <Shimmer>
              <SessionBreakInputShimmer />
            </Shimmer>
          )}
        </Container>
      ) : (
        <Container onMouseLeave={handleFlushDebounce} hidden={hidden}>
          {!isAdding ? (
            <SessionBreakInput
              ref={inputRef}
              autoFocus
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setSessionBreakInputOpen(false);
              }}
              onChange={(e) => {
                setSessionBreakInputValueFunction(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              value={sessionBreakInputValue}
              placeholder='Add Session Break Title'
            />
          ) : (
            <Shimmer>
              <SessionBreakInputShimmer />
            </Shimmer>
          )}
        </Container>
      )}
    </>
  );
};

SessionBreakTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  checkBoxInput: PropTypes.bool,
  setSessionBreakInputOpen: PropTypes.func,
  inputBox: PropTypes.bool,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
  hidden: PropTypes.bool,
  sessionBreakInputValue: PropTypes.string,
  setSessionBreakInputValue: PropTypes.func,
};

SessionBreakTable.defaultProps = {
  checkBoxInput: false,
  inputBox: false,
  setSessionBreakInputOpen: () => {},
  date: null,
  dayNumber: null,
  hidden: false,
  sessionBreakInputValue: '',
  setSessionBreakInputValue: () => {},

};

export default SessionBreakTable;
